import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { 
  RefreshCw,
  MapPin,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Globe,
  MessageSquare,
  Star
} from 'lucide-react'
import { syncReviews, syncAccountsAndLocations } from '@/utils/gmbSync'
import { SyncStatus } from '@/components/gmb/shared/SyncStatus'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { GmbLocation, GmbSyncLog, SyncResult } from '@/types/gmb'
import { useToast } from '@/hooks/use-toast'

interface LocationSyncStatus {
  locationId: string
  status: 'idle' | 'syncing' | 'success' | 'error'
  message?: string
  count?: number
}

export default function GMBReviews() {
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [locationSyncStatuses, setLocationSyncStatuses] = useState<Record<string, LocationSyncStatus>>({})
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch GMB locations
  const { data: gmbLocations = [], isLoading: locationsLoading, error: locationsError } = useQuery({
    queryKey: ['gmb-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_locations')
        .select('*')
        .eq('is_active', true)
        .order('location_name')
      
      if (error) throw error
      return data as GmbLocation[]
    }
  })

  // Fetch sync logs
  const { data: syncLogs = [] } = useQuery({
    queryKey: ['gmb-sync-logs', 'reviews'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_sync_log')
        .select('*')
        .eq('sync_type', 'reviews')
        .order('started_at', { ascending: false })
        .limit(20)
      
      if (error) throw error
      return data as GmbSyncLog[]
    }
  })

  // Fetch review stats by location
  const { data: reviewStats } = useQuery({
    queryKey: ['gmb-review-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select('gmb_location_id, source')
        .eq('source', 'gmb')
        .not('gmb_location_id', 'is', null)
      
      if (error) {
        console.warn('Failed to fetch GMB review stats:', error)
        return {}
      }
      
      // Count reviews by location
      const stats: Record<string, number> = {}
      data?.forEach(review => {
        if (review.gmb_location_id) {
          stats[review.gmb_location_id] = (stats[review.gmb_location_id] || 0) + 1
        }
      })
      
      return stats
    }
  })

  // Sync accounts mutation
  const syncAccountsMutation = useMutation({
    mutationFn: syncAccountsAndLocations,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['gmb-locations'] })
      queryClient.invalidateQueries({ queryKey: ['gmb-sync-logs'] })
      
      if (result.success) {
        toast({
          title: 'Accounts synced successfully',
          message: `Synced ${result.itemsSynced} items`,
          type: 'success'
        })
      } else {
        toast({
          title: 'Sync completed with errors',
          message: `${result.itemsSynced} succeeded, ${result.itemsFailed} failed`,
          type: 'error'
        })
      }
    },
    onError: (error) => {
      toast({
        title: 'Sync failed',
        message: error.message,
        type: 'error'
      })
    }
  })

  // Sync reviews mutation
  const syncReviewsMutation = useMutation({
    mutationFn: async (locationIds?: string[]) => {
      // Reset statuses
      const newStatuses: Record<string, LocationSyncStatus> = {}
      const locationsToSync = locationIds || gmbLocations.map(l => l.gmb_api_location_id || l.gmb_location_id)
      
      locationsToSync.forEach(id => {
        newStatuses[id] = { locationId: id, status: 'syncing' }
      })
      setLocationSyncStatuses(newStatuses)
      
      const result = await syncReviews(locationIds)
      
      // Update statuses based on result
      if (result.success) {
        locationsToSync.forEach(id => {
          newStatuses[id] = { 
            locationId: id, 
            status: 'success',
            message: 'Reviews synced successfully',
            count: reviewStats?.[id] || 0
          }
        })
      } else {
        // Parse errors to update individual location statuses
        result.errors.forEach(error => {
          const locationMatch = error.match(/Location (accounts\/\d+\/locations\/\d+):/)
          if (locationMatch) {
            const locationId = locationMatch[1]
            newStatuses[locationId] = {
              locationId,
              status: 'error',
              message: error
            }
          }
        })
      }
      
      setLocationSyncStatuses(newStatuses)
      return result
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      queryClient.invalidateQueries({ queryKey: ['gmb-review-stats'] })
      queryClient.invalidateQueries({ queryKey: ['gmb-sync-logs'] })
      
      if (result.success) {
        toast({
          title: 'Reviews synced successfully',
          message: `Synced ${result.itemsSynced} reviews`,
          type: 'success'
        })
      } else {
        const hasOAuthErrors = result.errors.some(error => 
          error.includes('Missing GMB API location ID') || 
          error.includes('OAuth')
        )
        
        if (hasOAuthErrors) {
          toast({
            title: 'GMB OAuth Required',
            message: 'Complete GMB OAuth setup first to enable review sync',
            type: 'warning'
          })
        } else {
          toast({
            title: 'Sync completed with errors',
            message: `${result.itemsSynced} succeeded, ${result.itemsFailed} failed`,
            type: 'error'
          })
        }
      }
    },
    onError: (error) => {
      toast({
        title: 'Sync failed',
        message: error.message,
        type: 'error'
      })
      
      // Reset all statuses to idle
      setLocationSyncStatuses({})
    }
  })

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocations(prev => 
      prev.includes(locationId) 
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    )
  }

  const handleSelectAll = () => {
    if (selectedLocations.length === gmbLocations.length) {
      setSelectedLocations([])
    } else {
      setSelectedLocations(gmbLocations.map(l => l.gmb_api_location_id || l.gmb_location_id))
    }
  }

  const handleSync = async (syncType: 'accounts' | 'reviews' | 'all') => {
    if (syncType === 'accounts' || syncType === 'all') {
      await syncAccountsMutation.mutateAsync()
    }
    
    if (syncType === 'reviews' || syncType === 'all') {
      const locationIds = selectedLocations.length > 0 ? selectedLocations : undefined
      await syncReviewsMutation.mutateAsync(locationIds)
    }
  }

  const isSyncing = syncAccountsMutation.isPending || syncReviewsMutation.isPending

  if (locationsError) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading GMB locations</AlertTitle>
          <AlertDescription>
            {locationsError.message}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">GMB Review Sync</h1>
          <p className="text-muted-foreground">
            Sync Google My Business reviews to your database
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSync('accounts')}
            disabled={isSyncing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncAccountsMutation.isPending ? 'animate-spin' : ''}`} />
            Sync Accounts
          </Button>
          <Button
            size="sm"
            onClick={() => handleSync('reviews')}
            disabled={isSyncing || gmbLocations.length === 0}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncReviewsMutation.isPending ? 'animate-spin' : ''}`} />
            Sync Reviews
          </Button>
        </div>
      </div>

      {/* Location Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>GMB Locations</CardTitle>
              <CardDescription>
                Select locations to sync reviews from
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              disabled={locationsLoading || gmbLocations.length === 0}
            >
              {selectedLocations.length === gmbLocations.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {locationsLoading ? (
            <div className="text-center py-8">
              <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2 animate-pulse" />
              <p className="text-muted-foreground">Loading locations...</p>
            </div>
          ) : gmbLocations.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No GMB locations found</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => handleSync('accounts')}
              >
                Sync Accounts & Locations
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {gmbLocations.map((location) => {
                const locationKey = location.gmb_api_location_id || location.gmb_location_id
                const isSelected = selectedLocations.includes(locationKey)
                const syncStatus = locationSyncStatuses[locationKey]
                const reviewCount = reviewStats?.[locationKey] || 0
                
                return (
                  <div
                    key={locationKey}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground'
                    }`}
                    onClick={() => handleLocationToggle(locationKey)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium">{location.location_name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {location.address_street}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {syncStatus?.status === 'syncing' && (
                          <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
                        )}
                        {syncStatus?.status === 'success' && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                        {syncStatus?.status === 'error' && (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="h-4 w-4 text-primary"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span>{location.average_rating || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4 text-muted-foreground" />
                          <span>{location.total_reviews || 0} total</span>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        <Globe className="w-3 h-3 mr-1" />
                        {reviewCount} synced
                      </Badge>
                    </div>
                    
                    {location.franchise_region && (
                      <Badge variant="outline" className="text-xs mt-2">
                        {location.franchise_region}
                      </Badge>
                    )}
                    
                    {syncStatus?.message && syncStatus.status === 'error' && (
                      <p className="text-xs text-red-600 mt-2">{syncStatus.message}</p>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync Status */}
      <SyncStatus
        syncLogs={syncLogs}
        isSyncing={isSyncing}
        syncProgress={isSyncing ? 50 : 0}
        onSync={async (syncType) => {
          if (syncType === 'reviews') {
            await handleSync('reviews')
          } else if (syncType === 'all') {
            await handleSync('all')
          }
        }}
        showDetailedLogs={true}
      />

      {/* Duplicate Detection Info */}
      <Card>
        <CardHeader>
          <CardTitle>Duplicate Detection</CardTitle>
          <CardDescription>
            Reviews are automatically deduplicated based on their GMB Review ID
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Automatic Deduplication</p>
                <p className="text-sm text-muted-foreground">
                  Each GMB review has a unique ID that prevents duplicate imports
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-blue-500 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Source Tracking</p>
                <p className="text-sm text-muted-foreground">
                  All GMB reviews are marked with source='gmb' for easy filtering
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-purple-500 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Location Mapping</p>
                <p className="text-sm text-muted-foreground">
                  GMB locations are automatically mapped to franchise regions
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}